import { lazy, Suspense, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';

// The field app is its own chunk: its pages load only when someone opens /field.
const FieldLayout = lazy(() => import('./FieldLayout'));
const FieldTodayPage = lazy(() => import('./pages/FieldWorkPages').then((m) => ({ default: m.FieldTodayPage })));
const FieldWorkOrderPage = lazy(() => import('./pages/FieldWorkPages').then((m) => ({ default: m.FieldWorkOrderPage })));
const FieldDuePage = lazy(() => import('./pages/FieldOtherPages').then((m) => ({ default: m.FieldDuePage })));
const FieldScanPage = lazy(() => import('./pages/FieldOtherPages').then((m) => ({ default: m.FieldScanPage })));
const FieldMapPage = lazy(() => import('./pages/FieldOtherPages').then((m) => ({ default: m.FieldMapPage })));
const FieldMorePage = lazy(() => import('./pages/FieldOtherPages').then((m) => ({ default: m.FieldMorePage })));
const FieldInspectionPage = lazy(() => import('./pages/FieldInspectionPage'));

const wait = (element: ReactNode) => (
  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>{element}</Suspense>
);

/** The field app (docs §215): signed in, in its own mobile frame, for the Property module and the field-work, maintenance, planning or contractor right. */
export const fieldRoutes: RouteObject[] = [
  {
    path: 'field',
    element: wait(
      <RequirePermission anyOf={[Perm.Property.FieldWork, Perm.Property.Maintenance, Perm.Property.PlanWork, Perm.Property.Contractor]}>
        <RequireModule module={Modules.Property}><FieldLayout /></RequireModule>
      </RequirePermission>,
    ),
    children: [
      { index: true, element: wait(<FieldTodayPage />) },
      { path: 'order/:id', element: wait(<FieldWorkOrderPage />) },
      { path: 'due', element: wait(<FieldDuePage />) },
      { path: 'inspection/:id', element: wait(<FieldInspectionPage />) },
      { path: 'scan', element: wait(<FieldScanPage />) },
      { path: 'map', element: wait(<FieldMapPage />) },
      { path: 'more', element: wait(<FieldMorePage />) },
    ],
  },
];
